import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  Tabs,
  Tab,
} from '@material-ui/core'
import { ReactComponent as CloseIcon } from 'assets/svg/CloseIcon.svg'
import { ReactComponent as PlusSquareIcon } from 'assets/svg/PlustSquareIcon.svg'
import { ReactComponent as DeleteTrashIcon } from 'assets/svg/DeleteTrashIcon.svg'
import { ICustomPayment, ICustomPaymentSchedule } from 'typescript/interfaces/deals'
import DecimalTextField from 'UI/TextField/DecimalTextField'
import ModalComponent from 'UI/Modal'
import ConfirmationContent from 'UI/Modal/ConfirmationContent'
import DatePicker from 'UI/DatePicker/DatePicker'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import { isBefore, startOfDay } from 'date-fns/esm'
import isValidDate from 'utils/isValidDate'
import convertDateForPicker from 'utils/convertDateForPicker'
import { calculateCustomScheduleDate } from 'utils/scheduleUtils'
import { isDateInPast, isSameCalendarDate } from 'utils/dateUtils'
import { FIRST_PAYMENT_DATE_EARLIER, FIRST_PAYMENT_DATE_IN_PAST } from 'constants/errors'
import { useCustomizedScheduleModalStyles } from './styles'

interface Props {
  open: boolean
  onClose: () => void
  onSave: (schedule: ICustomPaymentSchedule, firstPaymentDate: string) => void
  onError: (message: string) => void
  initialSchedule?: ICustomPaymentSchedule
  totalPaybackAmount: number
  dealStartDate?: string
  initialFirstPaymentDate?: string
  isFirstPaymentDateEditable: boolean
}

interface TabPanelProps {
  children?: React.ReactNode
  index: any
  value: any
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`frequency-tabpanel-${index}`}
      aria-labelledby={`frequency-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </div>
  )
}

const CustomizedScheduleModal = ({
  open,
  onClose,
  onSave,
  onError,
  initialSchedule,
  totalPaybackAmount,
  dealStartDate,
  initialFirstPaymentDate,
  isFirstPaymentDateEditable = true
}: Props) => {
  const classes = useCustomizedScheduleModalStyles()
  const [frequency, setFrequency] = useState<'Daily' | 'Weekly' | 'Biweekly'>('Daily')
  const [payments, setPayments] = useState<ICustomPayment[]>([])
  const [firstPaymentDate, setFirstPaymentDate] = useState<string | undefined>()
  const [nextId, setNextId] = useState(1)
  const [showCloseConfirm, setShowCloseConfirm] = useState(false)
  const [dateError, setDateError] = useState<string | null>(null);
  const addButtonRef = useRef<HTMLButtonElement | null>(null)
  const shouldScrollToLastRef = useRef(false)
  const initialStateRef = useRef<{
    frequency: 'Daily' | 'Weekly' | 'Biweekly'
    payments: ICustomPayment[],
    firstPaymentDate: string | undefined
  } | null>(null)

  const isDirty = useMemo(() => {
    if (!initialStateRef.current) return false
    if (frequency !== initialStateRef.current.frequency) return true
    if (firstPaymentDate !== initialStateRef.current.firstPaymentDate) return true
    return JSON.stringify(payments) !== JSON.stringify(initialStateRef.current.payments)
  }, [frequency, payments, firstPaymentDate])

  const totalPaidAmount = useMemo(() => {
    return payments.reduce((sum, payment) => sum + (payment.amount || 0), 0)
  }, [payments])

  const hasProcessedPayments = useMemo(() => payments.some(p => p.isProcessed), [payments])

  const remainingAmount = useMemo(() => {
    return totalPaybackAmount - totalPaidAmount
  }, [totalPaybackAmount, totalPaidAmount])

  const remainingPercentage = useMemo(() => {
    if (totalPaybackAmount === 0) return 0
    const percentage = (remainingAmount / totalPaybackAmount) * 100
    return Math.ceil(percentage * 10000) / 10000
  }, [totalPaybackAmount, remainingAmount])

  const handleFrequencyChange = (event: React.ChangeEvent<{}>, newValue: 'Daily' | 'Weekly' | 'Biweekly') => {
    setFrequency(newValue)
  }

  const handlePaymentChange = useCallback((id: number, field: 'amount', value: number) => {
    setPayments(prev => {
      const updated = prev.map(payment => {
        if (payment.id === id && !payment.isProcessed) {
          const otherPaymentsTotal = prev.reduce((sum, p) => p.id !== id ? sum + (p.amount || 0) : sum, 0)
          const maxAllowedAmount = totalPaybackAmount - otherPaymentsTotal
          const newAmount = Math.min(value, maxAllowedAmount)
          const percentage = totalPaybackAmount > 0 ? (newAmount / totalPaybackAmount) * 100 : 0
          const ceiledPercentage = Math.ceil(percentage * 10000) / 10000
          return { ...payment, [field]: newAmount, percentage: ceiledPercentage }
        }
        return payment
      })
      return updated
    })
  }, [totalPaybackAmount])

  const isPastDateAllowed = useCallback(
    (date: Date) => isSameCalendarDate(date, initialFirstPaymentDate),
    [initialFirstPaymentDate]
  )

  const handleFirstPaymentDateChange = useCallback((value: MaterialUiPickersDate) => {
    const converted = convertDateForPicker(value) as Date | null
      if (converted && isValidDate(converted)) {

        if (dealStartDate && isBefore(startOfDay(converted), startOfDay(new Date(dealStartDate)))) {
          setDateError(FIRST_PAYMENT_DATE_EARLIER);
          setFirstPaymentDate(undefined);
        } else if (isDateInPast(converted) && !isPastDateAllowed(converted)) {
          setDateError(FIRST_PAYMENT_DATE_IN_PAST);
          setFirstPaymentDate(undefined);
        } else {
          setDateError(null);
          setFirstPaymentDate(converted.toISOString());
        }
      } else {
        setDateError("MM/DD/YYYY");
        setFirstPaymentDate(undefined);
      }
  }, [dealStartDate, isPastDateAllowed])

  const handleAddPayment = useCallback(() => {
    setPayments(prev => {
      const newPayment: ICustomPayment = {
        id: nextId,
        order: prev.length + 1,
        amount: 0,
        percentage: 0,
        isProcessed: false,
        scheduledDate: calculateCustomScheduleDate(prev.length, firstPaymentDate, frequency) 
      }
      return [...prev, newPayment];
    })
    setNextId(prev => prev + 1)
    shouldScrollToLastRef.current = true
  }, [nextId, firstPaymentDate, frequency])

  useEffect(() => {
    if (shouldScrollToLastRef.current && addButtonRef.current) {
      addButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      shouldScrollToLastRef.current = false
    }
  }, [payments.length])

  const handleDeletePayment = useCallback((id: number) => {
    setPayments(prev => {
      if (prev.length <= 1) return prev
      const updated = prev.filter(payment => payment.id !== id)
      return updated.map((payment, index) => ({
        ...payment,
        order: index + 1,
        scheduledDate: calculateCustomScheduleDate(index, firstPaymentDate, frequency) 
      }))
    })
  }, [firstPaymentDate, frequency]) 

  const handleDistributeRemaining = useCallback(() => {
    const emptyPayments = payments.filter(p => (!p.amount || p.amount === 0) && !p.isProcessed)
    if (emptyPayments.length === 0) return

    const amountPerPayment = remainingAmount / emptyPayments.length
    const lastPaymentAdjustment = remainingAmount - (amountPerPayment * (emptyPayments.length - 1))

    setPayments(prev => {
      return prev.map(payment => {
        if (emptyPayments.some(ep => ep.id === payment.id)) {
          const isLast = payment.id === emptyPayments[emptyPayments.length - 1].id
          const amount = isLast ? lastPaymentAdjustment : amountPerPayment
          const percentage = totalPaybackAmount > 0 ? (amount / totalPaybackAmount) * 100 : 0
          const ceiledPercentage = Math.ceil(percentage * 10000) / 10000
          return {
            ...payment,
            amount: amount,
            percentage: ceiledPercentage,
          }
        }
        return payment
      })
    })
  }, [payments, remainingAmount, totalPaybackAmount])

  const handleSave = useCallback(() => {
    if (!firstPaymentDate) {
      onError("The first payment date is required and cannot be earlier than the deal start date.");
      return;
    } 
    const totalDistributed = totalPaidAmount
    const tolerance = 0.01

    if (Math.abs(totalDistributed - totalPaybackAmount) > tolerance) {
      const isLess = totalDistributed < totalPaybackAmount
      const errorMessage = `You distributed ${isLess ? 'less' : 'more'} than the Payback Amount. Please review the schedule to resolve the error.`
      onError(errorMessage)
      return
    }

    const schedule: ICustomPaymentSchedule = {
      frequency,
      payments: payments.filter(p => p.amount > 0),
    }

    onSave(schedule, firstPaymentDate)
  }, [frequency, payments, totalPaybackAmount, totalPaidAmount, onSave, onError, firstPaymentDate])

  useEffect(() => {
    if (open) {
      let initFrequency: 'Daily' | 'Weekly' | 'Biweekly' = 'Daily'
      let initPayments: ICustomPayment[]

      if (initialSchedule) {
        initFrequency = initialSchedule.frequency
        initPayments = initialSchedule.payments.map(payment => {
          const percentage = totalPaybackAmount > 0 ? (payment.amount / totalPaybackAmount) * 100 : 0
          const ceiledPercentage = Math.ceil(percentage * 10000) / 10000
          return {
            ...payment,
            percentage: ceiledPercentage,
          }
        })

        setNextId(Math.max(...initialSchedule.payments.map(p => p.id), 0) + 1)
      } else {
        initPayments = [{ id: 1, order: 1, amount: 0, percentage: 0, isProcessed: false, scheduledDate: initialFirstPaymentDate }]
        setNextId(2)
      }
      setFrequency(initFrequency)
      setPayments(initPayments)
      setFirstPaymentDate(initialFirstPaymentDate)
      initialStateRef.current = { frequency: initFrequency, payments: initPayments, firstPaymentDate: initialFirstPaymentDate }
      setDateError(null);
      setShowCloseConfirm(false)
    }
  }, [open, initialSchedule, totalPaybackAmount, dealStartDate, initialFirstPaymentDate])

  useEffect(() => {
    setPayments(prev => {
      let hasChanges = false;
      const nextPayments = prev.map((payment, index) => {
        const expectedDate = calculateCustomScheduleDate(index, firstPaymentDate, frequency);
        if (payment.scheduledDate !== expectedDate) {
          hasChanges = true;
          return { ...payment, scheduledDate: expectedDate };
        }
        return payment;
      });
      return hasChanges ? nextPayments : prev; 
    });
  }, [firstPaymentDate, frequency]);

  const handleCloseAttempt = useCallback(() => {
    if (isDirty) {
      setShowCloseConfirm(true)
    } else {
      onClose()
    }
  }, [isDirty, onClose])

  const handleConfirmClose = useCallback(() => {
    setShowCloseConfirm(false)
    onClose()
  }, [onClose])

  const handleCancelClose = useCallback(() => {
    setShowCloseConfirm(false)
  }, [])

  return (
    <>
    <Dialog open={open} onClose={handleCloseAttempt} maxWidth="md" fullWidth>
      <DialogTitle className={classes.dialogTitle}>
        <Box className={classes.dialogTitleContent}>
          <Typography variant="h6" className={classes.dialogTitleText}>
            Add Customised Schedule
          </Typography>
          <IconButton onClick={handleCloseAttempt} size="small" className={classes.closeButton}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box mb={3}>
          <Tabs
            value={frequency} 
            onChange={handleFrequencyChange} 
            aria-label="payment frequency tabs"
            TabIndicatorProps={{ style: { display: 'none' } }}
            className={classes.tabsContainer}
          >
            <Tab
              label="Daily"
              value="Daily"
              className={classes.tab}
              disabled={hasProcessedPayments}
            />
            <Tab
              label="Weekly"
              value="Weekly"
              className={classes.tab}
              disabled={hasProcessedPayments}
            />
            <Tab
              label="Biweekly"
              value="Biweekly"
              className={classes.tab}
              disabled={hasProcessedPayments}
            />
          </Tabs>
        </Box>

        <TabPanel value={frequency} index={frequency}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Payback Amount
              </Typography>
              <Box className={classes.paybackAmountContainer}>
                <Box className={classes.paybackAmountFields}>
                  <TextField
                    value={remainingAmount.toFixed(2)}
                    disabled
                    variant="outlined"
                    size="small"
                    className={classes.paybackAmountField}
                  />
                  <TextField
                    value={`${remainingPercentage.toFixed(4)}%`}
                    disabled
                    variant="outlined"
                    size="small"
                    className={classes.percentageField}
                  />
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDistributeRemaining}
                  disabled={remainingAmount <= 0}
                  className={classes.distributeButton}
                >
                  Distribute the remaining payments
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} className={classes.sumToPayContainer}>
              <Box>
                {payments.map((payment, index) => {
                  const isFirstIteration = index === 0;

                  return(
                    <Box
                      key={payment.id}
                      className={classes.paymentRow}
                    >
                      <Typography variant="body2" className={classes.paymentLabel}>
                        Payment {payment.order}
                      </Typography>
                      <DatePicker
                        value={payment.scheduledDate ? new Date(payment.scheduledDate) : null}
                        type="keyboard"
                        shouldDisableDate={isFirstIteration ? ((date) => {
                          if (!date) return false;
                          if (isDateInPast(date) && !isPastDateAllowed(date)) return true;
                          if (!dealStartDate) return false;
                          return isBefore(startOfDay(date), startOfDay(new Date(dealStartDate)));
                        }) : undefined}
                        onChange={isFirstIteration ? handleFirstPaymentDateChange : () => {}}
                        disabled={!isFirstIteration || !isFirstPaymentDateEditable || payment.isProcessed}
                        error={isFirstIteration ? Boolean(dateError) : false}
                        helperText={isFirstIteration ? dateError : ''}
                      />
                      <DecimalTextField
                        value={payment.amount || ''}
                        onChange={e => handlePaymentChange(payment.id, 'amount', Number(e.target.value) || 0)}
                        variant="outlined"
                        size="small"
                        placeholder="Sum to pay"
                        className={classes.paymentAmountField}
                        disabled={payment.isProcessed}
                      />
                      <TextField
                        value={`${payment.percentage.toFixed(4)}%`}
                        disabled
                        variant="outlined"
                        size="small"
                        className={classes.paymentPercentageField}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleDeletePayment(payment.id)}
                        disabled={payment.isProcessed || payments.length <= 1}
                        className={`${classes.deleteButton} ${(payment.isProcessed || payments.length <= 1) ? 'disabled' : ''}`}
                      >
                        <DeleteTrashIcon />
                      </IconButton>
                    </Box>
                  )
                })}
              </Box>
              <Button
                ref={addButtonRef}
                variant="outlined"
                startIcon={<PlusSquareIcon />}
                onClick={handleAddPayment}
                className={classes.addPaymentButton}
              >
                Add Payment
              </Button>
            </Grid>
          </Grid>
        </TabPanel>
      </DialogContent>
      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleCloseAttempt}
          className={classes.cancelButton}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          className={classes.saveButton}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
    <ModalComponent open={showCloseConfirm} onClose={handleCancelClose} style={{ zIndex: 1400 }}>
      <ConfirmationContent
        handleCancel={handleCancelClose}
        handleConfirm={handleConfirmClose}
        text="Are you sure you want to close? Your changes will not be saved."
        confirmText="Close"
        cancelText="Keep editing"
      />
    </ModalComponent>
    </>
  )
}

export default CustomizedScheduleModal
